/**
 * The one wording function for periods.
 *
 * The queue cell, the clock pill and the case header all call `readPeriod`, so
 * they cannot disagree about a date. In the prototype they each did their own
 * arithmetic and an expired Rule 14(4) period read *Due soon · 0 days remaining*
 * on one surface and overdue on another — the officer's own screen contradicting
 * itself about whether a statutory period had run out.
 *
 * **It never computes the state.** The state comes from the server, which owns
 * the organisation's date in `Asia/Kolkata`; the browser's date is not the
 * office's date and a period that reads differently on two desks is worse than
 * no period at all. What this function owns is the English.
 *
 * Two of the six are easy to get wrong and both are pinned in `period.test.ts`:
 *
 * `cannot-start` names the **act** that has not happened, never the field. "The
 * articles of charge have not been served" is an instruction; "start_date is
 * null" tells the officer the software is broken, and a case that reads as
 * broken goes quiet for six months.
 *
 * A due-soon period at zero days reads **"— due today."** and never "0 days
 * remaining", which is a different instruction wearing the same words.
 */

export type PeriodState =
  'cannot-start' | 'running' | 'due-soon' | 'overdue' | 'satisfied' | 'paused'

/** How the surface should colour it. Not a severity — a reading. */
export type PeriodTone = 'neutral' | 'accent' | 'warn' | 'bad' | 'good'

/**
 * What a surface needs from a period. Deliberately a subset of `ClockView`, so
 * the queue — which carries the worst clock's fields flattened onto the row —
 * can call the same function as the case page.
 */
export interface PeriodInput {
  state?: string | null
  days_left?: number | null
  blocked_reason?: string | null
  satisfied_on?: string | null
  closed_late_by?: number | null
  paused_from?: string | null
  pause_reason?: string | null
}

export interface Period {
  state: PeriodState | 'unknown'
  /** For a pill or a queue cell. Short, and never a bare number. */
  short: string
  /** The full reading, for a clock row or a header. Ends in a full stop. */
  sentence: string
  tone: PeriodTone
  /** True where the officer is being told about time that has run out. */
  urgent: boolean
}

const PERIOD_STATES: readonly string[] = [
  'cannot-start',
  'running',
  'due-soon',
  'overdue',
  'satisfied',
  'paused',
]

function plural(n: number, word: string): string {
  return n === 1 ? `1 ${word}` : `${n} ${word}s`
}

/** `2026-08-09` → `9 Aug 2026`. Chrome format; documents use DD / MM / YYYY. */
export function readDate(iso: string | null | undefined): string {
  if (!iso) return ''
  const [y, m, d] = iso.split('-').map(Number)
  if (!y || !m || !d) return iso
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ]
  return `${d} ${months[m - 1]} ${y}`
}

export function readPeriod(input: PeriodInput | null | undefined): Period {
  const raw = input?.state ?? null

  // A case with no clock at all is not a case with a clock in an unknown state.
  // It says so, rather than rendering an empty cell that reads as "fine".
  if (raw === null || raw === undefined || !PERIOD_STATES.includes(raw)) {
    return {
      state: 'unknown',
      short: 'No period',
      sentence: 'No period is running on this case.',
      tone: 'neutral',
      urgent: false,
    }
  }

  const state = raw as PeriodState
  const left = input?.days_left ?? null

  switch (state) {
    case 'cannot-start': {
      // The act, or — if the server sent none — a sentence that still says what
      // is wrong rather than nothing at all.
      const act = (input?.blocked_reason ?? '').trim()
      return {
        state,
        short: 'Cannot start',
        sentence:
          act || 'Cannot start — the act that begins this period has not been recorded.',
        tone: 'warn',
        urgent: false,
      }
    }

    case 'overdue': {
      const by = left === null ? null : Math.abs(left)
      return {
        state,
        short: by === null ? 'Overdue' : `Overdue by ${plural(by, 'day')}`,
        sentence: by === null ? 'Overdue.' : `Overdue by ${plural(by, 'day')}.`,
        tone: 'bad',
        urgent: true,
      }
    }

    case 'due-soon': {
      if (left === 0) {
        // Not "0 days remaining". A period expiring today is a different
        // instruction from one with days left, and the prototype's negative-zero
        // bug lived exactly here.
        return {
          state,
          short: 'Due today',
          sentence: '— due today.',
          tone: 'bad',
          urgent: true,
        }
      }
      const n = left ?? 0
      return {
        state,
        short: `${plural(n, 'day')} left`,
        sentence: `${plural(n, 'day')} remaining.`,
        tone: 'warn',
        urgent: true,
      }
    }

    case 'running': {
      const n = left ?? 0
      return {
        state,
        short: `${plural(n, 'day')} left`,
        sentence: `${plural(n, 'day')} remaining.`,
        tone: 'accent',
        urgent: false,
      }
    }

    case 'satisfied': {
      const on = readDate(input?.satisfied_on)
      const late = input?.closed_late_by ?? null
      // Closed late is still closed. The record has to show both that the act
      // came and that it came late — dropping the second is how a delay stops
      // existing the moment it is cured.
      const tail = late ? ` — ${plural(late, 'day')} late.` : '.'
      return {
        state,
        short: late ? `Satisfied · ${plural(late, 'day')} late` : 'Satisfied',
        sentence: on ? `Satisfied on ${on}${tail}` : `Satisfied${tail}`,
        tone: late ? 'warn' : 'good',
        urgent: false,
      }
    }

    case 'paused': {
      const from = readDate(input?.paused_from)
      const why = (input?.pause_reason ?? '').trim() || 'proceedings stayed'
      return {
        state,
        short: 'Paused',
        sentence: from ? `Paused from ${from} — ${why}.` : `Paused — ${why}.`,
        tone: 'neutral',
        urgent: false,
      }
    }
  }
}

/**
 * Queue ordering. Worst first, and **untrackable cases sort last among the open
 * ones rather than first**: a case whose period has not started is not urgent,
 * it is unstarted, and mixing it into the overdue list buries the ones that
 * really are running out.
 */
export const PERIOD_RANK: Record<string, number> = {
  overdue: 0,
  'due-soon': 1,
  running: 2,
  paused: 3,
  'cannot-start': 4,
  satisfied: 5,
}

export function periodRank(state: string | null | undefined): number {
  return PERIOD_RANK[state ?? ''] ?? 6
}
